import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { addMonths } from 'date-fns';
import * as d from 'date-fns';

import * as monthUtils from 'loot-core/shared/months';
import { integerToAmount } from 'loot-core/shared/util';

import { useCategories } from '../../../hooks/useCategories';
import { useGlobalPref } from '../../../hooks/useGlobalPref';
import { useNavigate } from '../../../hooks/useNavigate';
import { type CSSProperties, styles } from '../../../style';
import { Block } from '../../common/Block';
import { Link } from '../../common/Link';
import { View } from '../../common/View';
import { DateRange } from '../DateRange';
import { SpendingByCategoryGraph } from '../graphs/SpendingByCategoryGraph';
import { ReportCard } from '../ReportCard';
import { SpendingByCategorySpreadsheet } from '../spreadsheets/spending-by-category-spreadsheet';
import { useReport } from '../useReport';

type ISpendingByCategoryCardProps = {
  showViewAll?: boolean;
  cardStyles?: CSSProperties;
};

export function SpendingByCategoryCard({
  showViewAll,
  cardStyles = {},
}: ISpendingByCategoryCardProps) {
  const categories = useCategories(false);
  const [fiscalYearStart] = useGlobalPref('fiscalYearStart');
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const [categoriesSelected, setCategoriesSelected] = useState([]);

  const rangeStartDate = d
    .startOfMonth(new Date().setMonth(fiscalYearStart || 0))
    .toISOString();
  const rangeEndDate = d
    .endOfMonth(new Date().setMonth((fiscalYearStart || 0) + 11))
    .toISOString();

  const getGraphData = useMemo(() => {
    return SpendingByCategorySpreadsheet(
      categoriesSelected,
      startDate,
      endDate,
    );
  }, [startDate, endDate, categoriesSelected]);
  const rawGraphData = useReport('default', getGraphData);

  const categoriesById = useMemo(() => {
    let categoriesById = new Map();
    if (categories.list.length) {
      categoriesById = new Map(categories.list.map(c => [c.id, c]));
    }
    return categoriesById;
  }, [categories.list]);

  useEffect(() => {
    if (categories.list.length) {
      setCategoriesSelected(categories.list);
    }
  }, [categories.list]);

  useEffect(() => {
    const start = monthUtils.getMonthFromIndex(
      `${new Date().getFullYear()}`,
      fiscalYearStart || 0,
    );
    const end = addMonths(monthUtils.parseDate(start), 11);

    setStartDate(monthUtils.monthFromDate(start));
    setEndDate(monthUtils.monthFromDate(end));
  }, []);

  const incomeCategories = useMemo(() => {
    const incomeGroup = categories.grouped.find(g => g.is_income);
    return new Set(
      incomeGroup?.categories ? incomeGroup.categories.map(c => c.name) : [],
    );
  }, [categories.grouped]);

  const graphData = useMemo(() => {
    if (rawGraphData?.graphData && rawGraphData?.graphData.length) {
      const modifiedGraphData = [];
      // we have to create new array, because sort is mutable,
      // thus makes this useMemo cyclic (because it's a dependency)
      const sortedGraphData = [...rawGraphData?.graphData]?.sort((a, b) => {
        const firstActual = incomeCategories.has(a.name)
          ? a.actualAmount
          : a.actualAmount * -1;
        const secondActual = incomeCategories.has(b.name)
          ? b.actualAmount
          : b.actualAmount * -1;
        return firstActual - secondActual > 0 ? -1 : 1;
      });
      const categoriesSelected = [];
      for (const data of sortedGraphData) {
        if (!incomeCategories.has(data.name)) {
          modifiedGraphData.push({
            name: data.name,
            baseAmount: integerToAmount(data.actualAmount) * -1,
            compareToAmount: integerToAmount(data.budgetedAmount),
          });
          categoriesSelected.push(categoriesById.get(data.id));
        }
      }
      return modifiedGraphData;
    } else {
      return [];
    }
  }, [JSON.stringify(rawGraphData?.graphData)]);

  const navigationHandler = (category: string) => {
    if (category) {
      const categoryId =
        categories.list.find(cat => cat.name === category)?.id || null;
      if (categoryId) {
        const conditions = [
          {
            field: 'category',
            op: 'is',
            value: categoryId,
          },
          {
            field: 'date',
            op: 'gte',
            value: monthUtils.format(startDate, 'yyyy-MM-dd'),
          },
        ];
        navigate('/accounts', {
          state: {
            goBack: true,
            conditions,
            categoryId,
          },
        });
      }
    }
  };

  const navigateToDetailPage = () => {
    if (!showViewAll) {
      navigate('/reports/spending-by-category');
    }
  };

  return (
    <ReportCard
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'fit-Content',
        order: 5,
        ...cardStyles,
        cursor: showViewAll ? 'default' : 'pointer',
      }}
      flex={1}
    >
      <View onClick={navigateToDetailPage} style={{ flex: 1 }}>
        <View style={{ padding: 20 }}>
          <View
            style={{
              flex: 1,
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Block
              style={{ ...styles.mediumText, fontWeight: 500, marginBottom: 5 }}
              role="heading"
            >
              Spending by category
            </Block>
            {showViewAll && (
              <Link to="/reports/spending-by-category" variant="internal">
                View more
              </Link>
            )}
          </View>
          {rangeStartDate && rangeEndDate && (
            <DateRange start={rangeStartDate} end={rangeEndDate} />
          )}
        </View>
        <SpendingByCategoryGraph
          data={graphData}
          compact={true}
          isOverviewMode={true}
          onNavigate={navigationHandler}
        />
      </View>
    </ReportCard>
  );
}
